// src/components/ReferenceRedirect.jsx
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

const ReferenceRedirect = () => {
  const [searchParams] = useSearchParams();
  const recordId = searchParams.get('id');
  const [isLoading, setIsLoading] = useState(true);
  const [iframeError, setIframeError] = useState(false);
  const [referenceStatus, setReferenceStatus] = useState(null);

  const salesforceUrl = recordId 
    ? `https://cerebralstaffing.my.salesforce-sites.com/apex/PublicReferenceForm?id=${recordId}&_t=${Date.now()}`
    : null;

  useEffect(() => {
    if (!recordId) {
      // If no ID is provided, redirect to contact page as a fallback
      window.location.href = '/contact';
      return;
    }

    // Since API is not available in production, skip status check and just load iframe
    // The Salesforce form itself will handle showing the completion message
    console.log('Loading reference form for ID:', recordId);
    setIsLoading(false);
    setReferenceStatus({ success: true, isCompleted: false }); // Assume not completed, let iframe handle it
  }, [recordId]);

  // Listen for iframe height messages and scroll requests
  useEffect(() => {
    let lastHeight = 0;
    const handleMessage = (event) => {
      if (event.origin !== 'https://cerebralstaffing.my.salesforce-sites.com') {
        return;
      }
      
      // Handle iframe height changes
      if (event.data && event.data.type === 'iframeHeight') {
        const iframe = document.querySelector('iframe[title="Reference Form"]');
        if (iframe && Math.abs(event.data.height - lastHeight) > 10) { // Only resize if significant change
          const newHeight = event.data.height + 20;
          iframe.style.height = newHeight + 'px';
          lastHeight = event.data.height;
          console.log('Iframe resized to:', newHeight + 'px');
        }
      }
      
      // Handle scroll to top requests from Salesforce forms
      if (event.data && 
          event.data.type === 'scrollToTop' && 
          event.data.source === 'salesforce-reference-form') {
        console.log('Received scroll request from iframe, scrolling parent page');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const handleIframeLoad = () => {
    setIsLoading(false);
    setIframeError(false);
  };

  const handleIframeError = () => {
    setIsLoading(false);
    setIframeError(true);
  };

  const handleFallbackRedirect = () => {
    if (salesforceUrl) {
      window.open(salesforceUrl, '_blank');
    }
  };

  if (!recordId) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        fontFamily: 'Arial, sans-serif'
      }}>
        <h2>Missing Reference ID</h2>
        <p>No reference ID was provided. Redirecting to contact page...</p>
      </div>
    );
  }

  return (
    <div style={{ 
      width: '100%',
      minHeight: '100vh',
      position: 'relative'
    }}>
      {/* Loading Overlay */}
      {isLoading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'white',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          zIndex: 1000,
          fontFamily: 'Arial, sans-serif'
        }}>
          <h2 style={{ marginBottom: '20px', color: '#28346c' }}>
            Checking Reference Status...
          </h2>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #28346c',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
        </div>
      )}

      {/* Reference Already Completed */}
      {!isLoading && referenceStatus && referenceStatus.success === true && referenceStatus.isCompleted === true && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: '#f9f9f9',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          zIndex: 1000,
          fontFamily: 'Arial, sans-serif',
          padding: '20px'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(40, 52, 108, 0.08)',
            overflow: 'hidden',
            border: '1px solid rgba(40, 52, 108, 0.1)',
            maxWidth: '600px',
            width: '100%'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              padding: '2rem 1.5rem'
            }}>
              <h3 style={{ 
                fontSize: '1.5rem', 
                fontWeight: '600', 
                marginBottom: '0.5rem',
                display: 'flex',
                alignItems: 'center'
              }}>
                ✔️ Reference Already Submitted
              </h3>
              <p style={{ 
                fontSize: '0.95rem', 
                opacity: '0.9', 
                fontWeight: '400' 
              }}>
                This reference form has been completed
              </p>
            </div>
            <div style={{ padding: '2rem 1.5rem' }}>
              <p style={{ marginBottom: '1rem', fontSize: '1rem', color: '#374151' }}>
                Thank you! Your reference feedback for <strong>{referenceStatus.candidateName || 'this candidate'}</strong> has already been successfully submitted.
              </p>
              {referenceStatus.submittedDate && (
                <p style={{ marginBottom: '1rem', fontSize: '0.9rem', color: '#6b7280' }}>
                  Submitted on: {new Date(referenceStatus.submittedDate).toLocaleDateString()}
                </p>
              )}
              <p style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                You may now close this window.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error Fallback */}
      {iframeError && !isLoading && !(referenceStatus && referenceStatus.success === true && referenceStatus.isCompleted === true) && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'white',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          zIndex: 1000,
          fontFamily: 'Arial, sans-serif',
          padding: '20px'
        }}>
          <h2 style={{ color: '#28346c', marginBottom: '10px' }}>
            Unable to Load Reference Form
          </h2>
          <p style={{ marginBottom: '20px', textAlign: 'center' }}>
            The reference form cannot be displayed directly. Click below to open it in a new window.
          </p>
          <button
            onClick={handleFallbackRedirect}
            style={{
              backgroundColor: '#28346c',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            Open Reference Form
          </button>
        </div>
      )}

      {/* Iframe - only show if reference is not completed */}
      {salesforceUrl && !iframeError && !isLoading && !(referenceStatus && referenceStatus.success === true && referenceStatus.isCompleted === true) && (
        <iframe
          src={salesforceUrl}
          style={{
            width: '100%',
            height: '600px',
            border: 'none',
            display: 'block'
          }}
          onLoad={handleIframeLoad}
          onError={handleIframeError}
          title="Reference Form"
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-downloads"
        />
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ReferenceRedirect;